import { useCallback, useEffect, useState } from "react";
import CommunicationMessageComposer from "../communication/CommunicationMessageComposer";
import CommunicationThreadDetail from "../communication/CommunicationThreadDetail";
import CommunicationThreadList from "../communication/CommunicationThreadList";
import {
  createCommunicationMessage,
  createCommunicationThread,
  getCommunicationThread,
  listCommunicationThreads,
  markCommunicationThreadRead,
  sendCommunicationThreadPortalNotification,
  updateCommunicationThreadPortalVisibility,
  updateCommunicationThreadReplyPolicy,
  updateCommunicationThreadStatus,
} from "../../services/communicationService";

function getErrorMessage(error, fallback) {
  return (
    error?.apiError?.message ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

export default function ProjectCommunicationPanel({
  project,
  client,
  initialAttentionCount = 0,
  onAttentionCountChange,
}) {
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState("");
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [threadSaving, setThreadSaving] = useState(false);
  const [messageSaving, setMessageSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [markReadSaving, setMarkReadSaving] = useState(false);
  const [portalVisibilitySaving, setPortalVisibilitySaving] = useState(false);
  const [portalReplyPolicySaving, setPortalReplyPolicySaving] = useState(false);
  const [portalNotificationSending, setPortalNotificationSending] = useState(false);
  const [statusValue, setStatusValue] = useState("new");
  const [attentionCount, setAttentionCount] = useState(initialAttentionCount);

  const loadThreads = useCallback(async () => {
    if (!project?._id) return;
    try {
      setLoadingList(true);
      setError("");
      const nextThreads = await listCommunicationThreads({
        projectId: project._id,
        context: "project",
      });
      setThreads(nextThreads);
      const nextCount = nextThreads.filter((thread) => thread?.needsInternalAttention).length;
      setAttentionCount(nextCount);
      onAttentionCountChange?.(nextCount);
      setSelectedThreadId((current) => {
        if (current && nextThreads.some((thread) => String(thread._id) === String(current))) {
          return current;
        }
        return String(nextThreads[0]?._id || "");
      });
    } catch (loadError) {
      setError(getErrorMessage(loadError, "Projectcommunicatie laden mislukt."));
      setThreads([]);
      setAttentionCount(0);
      onAttentionCountChange?.(0);
      setSelectedThreadId("");
    } finally {
      setLoadingList(false);
    }
  }, [onAttentionCountChange, project?._id]);

  const loadThreadDetail = useCallback(async (threadId) => {
    if (!threadId) {
      setSelectedThread(null);
      setMessages([]);
      setStatusValue("new");
      return;
    }
    try {
      setLoadingDetail(true);
      setDetailError("");
      const detail = await getCommunicationThread(threadId, { context: "project" });
      setSelectedThread(detail.thread);
      setMessages(detail.messages);
      setStatusValue(detail.thread?.status || "new");
      if (detail?.meta?.attentionCleared) {
        await loadThreads();
      }
    } catch (loadError) {
      setSelectedThread(null);
      setMessages([]);
      setDetailError(getErrorMessage(loadError, "Communicatiedetail laden mislukt."));
    } finally {
      setLoadingDetail(false);
    }
  }, [loadThreads]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    loadThreadDetail(selectedThreadId);
  }, [loadThreadDetail, selectedThreadId]);

  async function handleCreateThread(payload) {
    if (!client?._id && !client?.id) {
      setError("Project heeft nog geen gekoppelde klant. Communicatie kan nog niet centraal worden opgeslagen.");
      throw new Error("CLIENT_REQUIRED");
    }
    try {
      setThreadSaving(true);
      setError("");
      const created = await createCommunicationThread({
        clientId: String(client?._id || client?.id),
        projectId: String(project?._id || ""),
        subject: payload.subject,
        status: "action_needed",
        participants:
          payload?.from?.name || payload?.from?.email
            ? [
                {
                  name: payload.from.name,
                  email: payload.from.email,
                  type: "customer",
                },
              ]
            : [],
        initialMessage: {
          direction: payload.direction,
          from: payload.from,
          bodyText: payload.bodyText,
        },
      });
      await loadThreads();
      const nextId = String(created?.thread?._id || "");
      setSelectedThreadId(nextId);
      if (nextId) {
        await loadThreadDetail(nextId);
      }
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Nieuw projectgesprek aanmaken mislukt."));
      throw saveError;
    } finally {
      setThreadSaving(false);
    }
  }

  async function handleAddMessage(payload) {
    if (!selectedThreadId) return;
    try {
      setMessageSaving(true);
      setDetailError("");
      await createCommunicationMessage(selectedThreadId, {
        direction: payload.direction,
        from: payload.from,
        bodyText: payload.bodyText,
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Bericht toevoegen mislukt."));
      throw saveError;
    } finally {
      setMessageSaving(false);
    }
  }

  async function handleSaveStatus() {
    if (!selectedThreadId) return;
    try {
      setStatusSaving(true);
      setDetailError("");
      await updateCommunicationThreadStatus(selectedThreadId, statusValue);
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Status wijzigen mislukt."));
    } finally {
      setStatusSaving(false);
    }
  }

  async function handleMarkRead() {
    if (!selectedThreadId) return;
    try {
      setMarkReadSaving(true);
      setDetailError("");
      await markCommunicationThreadRead(selectedThreadId, { context: "project" });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Markeren als gelezen mislukt."));
    } finally {
      setMarkReadSaving(false);
    }
  }

  async function handlePortalVisibilityToggle(nextVisible) {
    if (!selectedThreadId) return;
    try {
      setPortalVisibilitySaving(true);
      setDetailError("");
      await updateCommunicationThreadPortalVisibility(selectedThreadId, Boolean(nextVisible), {
        context: "project",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal-zichtbaarheid wijzigen mislukt."));
    } finally {
      setPortalVisibilitySaving(false);
    }
  }

  async function handlePortalReplyPolicyToggle(nextAllowReply) {
    if (!selectedThreadId) return;
    try {
      setPortalReplyPolicySaving(true);
      setDetailError("");
      await updateCommunicationThreadReplyPolicy(selectedThreadId, Boolean(nextAllowReply), {
        context: "project",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal reactiebeleid wijzigen mislukt."));
    } finally {
      setPortalReplyPolicySaving(false);
    }
  }

  async function handlePortalNotificationSend() {
    if (!selectedThreadId) return;
    try {
      setPortalNotificationSending(true);
      setDetailError("");
      await sendCommunicationThreadPortalNotification(selectedThreadId, {
        context: "project",
      });
      await loadThreads();
      await loadThreadDetail(selectedThreadId);
    } catch (saveError) {
      setDetailError(getErrorMessage(saveError, "Klantportaal notificatiemail versturen mislukt."));
    } finally {
      setPortalNotificationSending(false);
    }
  }

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-700">Alle communicatie die aan dit project is gekoppeld.</p>
          {attentionCount > 0 ? (
            <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
              {attentionCount} nieuwe klantreact{attentionCount === 1 ? "ie" : "ies"}
            </span>
          ) : null}
        </div>
      </div>

      {error ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{error}</div> : null}

      <CommunicationMessageComposer
        title="Nieuw projectgesprek"
        includeSubject
        submitLabel="Gesprek aanmaken"
        disabled={threadSaving}
        onSubmit={handleCreateThread}
      />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <CommunicationThreadList
          threads={threads}
          selectedThreadId={selectedThreadId}
          onSelect={(thread) => setSelectedThreadId(String(thread?._id || ""))}
          loading={loadingList}
          emptyText="Nog geen projectcommunicatie gekoppeld."
        />

        <CommunicationThreadDetail
          thread={selectedThread}
          messages={messages}
          loading={loadingDetail}
          detailError={detailError}
          statusValue={statusValue}
          onStatusChange={setStatusValue}
          onStatusSave={handleSaveStatus}
          statusSaving={statusSaving}
          canLinkProject={false}
          messageSaving={messageSaving}
          onSubmitMessage={handleAddMessage}
          markReadSaving={markReadSaving}
          onMarkRead={handleMarkRead}
          portalVisibilitySaving={portalVisibilitySaving}
          onPortalVisibilityToggle={handlePortalVisibilityToggle}
          portalReplyPolicySaving={portalReplyPolicySaving}
          onPortalReplyPolicyToggle={handlePortalReplyPolicyToggle}
          portalNotificationSending={portalNotificationSending}
          onPortalNotificationSend={handlePortalNotificationSend}
        />
      </div>
    </section>
  );
}
